import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { LogoImg } from "@/components/site/Logo";
import { api, errCode } from "@/lib/api";

const ERRORS = {
  invalid_credentials: "E-posta veya şifre hatalı.",
  locked: "Çok fazla hatalı deneme. Lütfen 15 dakika sonra tekrar deneyin.",
  not_allowed: "Bu Google hesabının yönetici paneline erişim izni yok.",
  invalid_session: "Google ile giriş tamamlanamadı. Lütfen tekrar deneyin.",
};

export const useAdminTitle = (title) => {
  useEffect(() => {
    document.title = `${title} | Home in Slovenia`;
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); }
    m.content = "noindex,nofollow";
  }, [title]);
};

export default function AdminLogin() {
  useAdminTitle("Yönetici girişi");
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error ? ERRORS[location.state.error] || ERRORS.invalid_session : "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => {}).finally(() => setChecking(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      navigate("/admin/applications", { state: { user: data } });
    } catch (err) {
      setError(ERRORS[errCode(err)] || "Giriş yapılamadı.");
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/applications";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-ivory"><Loader2 className="animate-spin text-forest" /></div>;
  if (authed) return <Navigate to="/admin/applications" replace />;

  const input = "mt-2 w-full rounded-xl border border-forest/20 bg-white px-4 py-3 text-forest outline-none focus:border-gold-deep";
  return (
    <div className="flex min-h-screen items-center justify-center bg-forest px-5 py-16">
      <div className="w-full max-w-md rounded-[28px] bg-ivory p-8 sm:p-10" data-testid="admin-login-card">
        <LogoImg className="h-20 w-auto" />
        <h1 className="mt-8 flex items-center gap-2 font-display text-4xl text-forest"><Lock size={22} /> Yönetici girişi</h1>
        <p className="mt-2 text-sm text-forest/70">Yalnızca yetkili yönetici erişebilir.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <label className="block text-sm font-medium text-forest">E-posta<input data-testid="admin-email-input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} /></label>
          <label className="block text-sm font-medium text-forest">Şifre<input data-testid="admin-password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={input} /></label>
          {error && <p role="alert" data-testid="admin-login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn btn-forest w-full">{busy && <Loader2 size={16} className="animate-spin" />} Giriş yap</button>
        </form>
        <div className="my-6 flex items-center gap-3 text-xs text-forest/60"><span className="h-px flex-1 bg-forest/15" />veya<span className="h-px flex-1 bg-forest/15" /></div>
        <button onClick={google} data-testid="admin-google-login" className="btn btn-outline w-full">Google ile giriş yap</button>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    api.post("/auth/google-session", {}, { headers: { "X-Session-ID": sid } })
      .then(({ data }) => navigate("/admin/applications", { replace: true, state: { user: data } }))
      .catch((err) => navigate("/admin", { replace: true, state: { error: errCode(err) } }));
  }, [location.hash, navigate]);
  return <div className="flex min-h-screen items-center justify-center bg-ivory"><Loader2 className="animate-spin text-forest" /></div>;
}
